/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.3.0
 * @since 1.0.1
 */

import { Fragment } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock } from '../../../../../../components/common';
import { SortEnum } from '../../../../../../components/Table/components/Filters/Filter';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { RoutePath } from '../../../../../../routes/constants/routesConstants';
import { SubscriberMobileField } from '../../../../../../types/subscriberTypes';
import { WidgetListStyled, WidgetSuccessStyled } from '../../styled/WidgetStyled';
import {
    EWidgetSearchAccountsField,
    IWidgetSearchAccountsItem,
} from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { widgetSubscriberMobileRedirect } from '../widgetSubscriberMobileActions';
import { EWidgetSubscriberMobile, IWidgetSubscriberMobileItem } from '../widgetSubscriberMobileTypes';
import WidgetSubscriberMobileListItem from './WidgetSubscriberMobileListItem';

interface IWidgetSubscriberMobileSuccessful {
    className?: string;
    field: EWidgetSubscriberMobile;
    items: IWidgetSubscriberMobileItem[];
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    translation: string;
}

interface IDispatchToProps {
    widgetSubscriberMobileRedirect: (payload: string) => void;
}

export type TWidgetSubscriberMobileSuccessful = IWidgetSubscriberMobileSuccessful & IConnectedProps & IDispatchToProps;

export const WidgetSubscriberMobileSuccessful = (props: TWidgetSubscriberMobileSuccessful) => {
    const {
        className,
        field,
        items,
        translation,
    } = props;

    const onLabelLinkClick = () => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMobile
                .onClickTabGroupWidgetSubscriberMobile(translation, EWidgetSubscriberMobile.LABELS),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const onTariffLinkClick = () => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMobile
                .onClickTabGroupWidgetSubscriberMobile(translation, EWidgetSubscriberMobile.TARIFFS),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const getUrlToSubscribers = (sortBy?: string) => {
        let sorting: any = {};
        if (sortBy) {
            sorting = {
                property: sortBy,
                direction: SortEnum.ascending,
            };
        }

        const filtering: any = {};
        if (props.selectedAccount[EWidgetSearchAccountsField.number]) {
            filtering.account = [{
                value: props.selectedAccount[EWidgetSearchAccountsField.number],
                label: props.selectedAccount[EWidgetSearchAccountsField.number],
            }];
        }

        const sort = encodeURIComponent(JSON.stringify(sorting));
        const filter = encodeURIComponent(JSON.stringify(filtering));
        return `${RoutePath.subscriberMobile}?s=${sort}&f=${filter}`;
    };

    return (
        <Fragment>
            {{
                labels:
                    <Fragment>
                        <WidgetSuccessStyled className={`widget-subscriber-mobile__success-labels ${className || ''}`}>
                            <WidgetListStyled>
                                <WidgetSubscriberMobileListItem
                                    field={field}
                                    items={items.slice(0, 5)}
                                    pushGtmAnalytic={props.pushGtmAnalytic}
                                    type={EWidgetSubscriberMobile.LABELS}
                                />
                            </WidgetListStyled>
                        </WidgetSuccessStyled>
                        <div>
                            <LinkBlock
                                dataName="onSetSort"
                                url={getUrlToSubscribers()}
                                onClick={onLabelLinkClick}
                            >
                                {translation}
                            </LinkBlock>
                        </div>
                    </Fragment>,
                tariffs:
                    <Fragment>
                        <WidgetSuccessStyled className={`widget-subscriber-mobile__success-tariff ${className || ''}`}>
                            <WidgetListStyled>
                                <WidgetSubscriberMobileListItem
                                    field={SubscriberMobileField.ratePlan}
                                    items={items.slice(0, 5)}
                                    pushGtmAnalytic={props.pushGtmAnalytic}
                                    type={EWidgetSubscriberMobile.TARIFFS}
                                />
                            </WidgetListStyled>
                        </WidgetSuccessStyled>
                        <div>
                            <LinkBlock
                                dataName="onSetSortWithRatePlan"
                                url={getUrlToSubscribers(SubscriberMobileField.ratePlan)}
                                onClick={onTariffLinkClick}
                            >
                                {translation}
                            </LinkBlock>
                        </div>
                    </Fragment>,
            }[field]}

        </Fragment>
    );
};

interface IConnectedProps {
    selectedAccount: IWidgetSearchAccountsItem;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchToProps => ({
    widgetSubscriberMobileRedirect: (payload: string) => dispatch(widgetSubscriberMobileRedirect(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetSubscriberMobileSuccessful);
